import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Smartphone, Settings2, Cloud, BarChart3, Shield } from 'lucide-react';
import Hero3D from '@/components/Hero3D';

// Brief service preview for homepage
const servicesPreviews = [
  { icon: Smartphone, title: 'Mobil Uygulama', color: '#00E5FF' },
  { icon: Settings2, title: 'Otomasyon', color: '#2962FF' },
  { icon: Cloud, title: 'Bulut Çözümleri', color: '#B026FF' },
  { icon: BarChart3, title: 'Veri Analitiği', color: '#00E5FF' },
  { icon: Shield, title: 'Siber Güvenlik', color: '#2962FF' },
];

function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen w-full flex items-center justify-center overflow-hidden">
        <Hero3D />
        
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background-primary/30 to-background-primary pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-background-primary/80 pointer-events-none" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center pointer-events-none">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-xs tracking-[0.3em] uppercase text-brand-cyan font-mono mb-6"
          >
            Yazılım Çözümleri
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black text-white leading-tight mb-6"
          >
            <span className="block">GEVEZ</span>
            <span className="block gradient-text">SOFTWARE</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="font-mono text-base md:text-lg text-text-secondary max-w-2xl mx-auto mb-12 leading-relaxed"
          >
            Ölçeklenebilir, modern ve güvenli yazılım çözümleri geliştiriyoruz.
            <br className="hidden md:block" />
            <span className="text-brand-cyan">Geleceği birlikte kodluyoruz.</span>
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.1 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center pointer-events-auto"
          >
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Link
                to="/iletisim"
                className="inline-block px-8 py-4 bg-brand-cyan text-background-primary font-display font-bold text-sm uppercase tracking-wider
                           hover:bg-white hover:shadow-[0_0_30px_rgba(0,229,255,0.6)] transition-all duration-300"
                data-testid="hero-demo-button"
              >
                Ücretsiz Demo Al
              </Link>
            </motion.div>

            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Link
                to="/hizmetler"
                className="inline-block px-8 py-4 border border-white/30 text-white font-display font-medium text-sm uppercase tracking-wider
                           hover:border-brand-cyan hover:text-brand-cyan hover:shadow-[0_0_20px_rgba(0,229,255,0.3)] transition-all duration-300"
                data-testid="hero-services-button"
              >
                Hizmetlerimiz
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Quick Services Preview */}
      <section className="py-20 bg-background-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-brand-cyan font-mono mb-4">
              Uzmanlık Alanlarımız
            </p>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white">
              Neler <span className="gradient-text">Yapıyoruz?</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
            {servicesPreviews.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="glass p-6 text-center group hover:border-brand-cyan/30 transition-all duration-300"
                >
                  <div 
                    className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-lg transition-colors"
                    style={{ background: `${service.color}15` }}
                  >
                    <Icon size={24} style={{ color: service.color }} />
                  </div>
                  <p className="font-mono text-sm text-text-secondary group-hover:text-white transition-colors">
                    {service.title}
                  </p>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Link
              to="/hizmetler"
              className="inline-flex items-center gap-2 text-brand-cyan font-mono text-sm hover:gap-4 transition-all duration-300"
              data-testid="view-all-services"
            >
              Tüm Hizmetleri Gör <ChevronRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-20 bg-background-primary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '50+', label: 'Mutlu Müşteri' },
              { value: '100+', label: 'Tamamlanan Proje' },
              { value: '10+', label: 'Yıllık Deneyim' },
              { value: '7/24', label: 'Destek Hizmeti' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <h3 className="font-display text-3xl md:text-4xl font-bold text-brand-cyan mb-2">
                  {stat.value}
                </h3>
                <p className="font-mono text-sm text-text-secondary">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-background-secondary relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-brand-cyan/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-brand-purple/10 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display text-2xl md:text-4xl font-bold text-white mb-6"
          >
            Projenizi <span className="gradient-text">Hayata Geçirelim</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-mono text-text-secondary mb-8"
          >
            İhtiyaçlarınızı dinliyor, en uygun çözümü sunuyoruz. Hemen iletişime geçin.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link
              to="/iletisim"
              className="inline-block px-10 py-4 bg-brand-cyan text-background-primary font-display font-bold text-sm uppercase tracking-wider
                         hover:bg-white hover:shadow-[0_0_30px_rgba(0,229,255,0.6)] transition-all duration-300"
              data-testid="cta-contact"
            >
              İletişime Geç
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
